import { Button, ButtonAnchor, Card, Heading, Icon, Text } from "@/components/ui"
import { fetchReferralLink, type ReferralLink } from "@/domain/marketing/api"
import {
  referralFriendBenefit,
  referralReferrerBenefit,
  referralShareMessage,
  referralWhatsAppUrl,
} from "@/domain/marketing/referral"
import { useAuthStatus } from "@/hooks/use-auth-status"
import { Copy, Gift, Trophy, WhatsappLogo } from "@/icons"
import { useEffect, useId, useRef, useState } from "react"

type CopyState = "idle" | "copied" | "manual"

const COPY_FEEDBACK: Record<CopyState, string> = {
  idle: "",
  copied: "Link copiado",
  manual: "Link selecionado. Copie com Ctrl+C ou segure para copiar.",
}

/** "Indique e ganhe" card: the customer's personal referral link (created on first open), copy and WhatsApp share; renders nothing without an active program. */
export function ReferralCard({ storeName }: { storeName?: string }) {
  const authenticated = useAuthStatus()
  const [link, setLink] = useState<ReferralLink | null>(null)

  useEffect(() => {
    if (!authenticated) return
    let cancelled = false
    void fetchReferralLink().then((found) => {
      if (!cancelled) setLink(found)
    })
    return () => {
      cancelled = true
    }
  }, [authenticated])

  if (!link) return null
  return <ReferralLinkPanel link={link} storeName={storeName} />
}

function ReferralLinkPanel({ link, storeName }: { link: ReferralLink; storeName?: string }) {
  const titleId = useId()
  const linkRef = useRef<HTMLElement>(null)
  const [copy, setCopy] = useState<CopyState>("idle")
  const whatsAppHref = referralWhatsAppUrl(referralShareMessage(storeName, link.url, link.welcomeBenefitText))

  function selectLink() {
    const node = linkRef.current
    const selection = window.getSelection()
    if (!node || !selection) return
    const range = document.createRange()
    range.selectNodeContents(node)
    selection.removeAllRanges()
    selection.addRange(range)
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(link.url)
      setCopy("copied")
    } catch {
      selectLink()
      setCopy("manual")
    }
  }

  return (
    <Card as="section" aria-labelledby={titleId} padding="lg" className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <Heading id={titleId} level={2} size="card">
          Indique e ganhe
        </Heading>
        <Text size="sm" tone="muted">
          Mande o seu link pessoal para os amigos pedirem{storeName ? ` na ${storeName}` : ""}.
        </Text>
      </div>

      <ul className="flex flex-col gap-2">
        <Text as="li" size="sm" tone="default" className="flex items-start gap-2">
          <Icon as={Gift} size="md" tone="primary" weight="bold" className="mt-0.5" />
          <span>{referralFriendBenefit(link.welcomeBenefitText)}</span>
        </Text>
        <Text as="li" size="sm" tone="default" className="flex items-start gap-2">
          <Icon as={Trophy} size="md" tone="primary" weight="bold" className="mt-0.5" />
          <span>{referralReferrerBenefit(link.rewardBenefitText)}</span>
        </Text>
      </ul>

      <div className="flex flex-col gap-1">
        <Text size="xs" weight="semibold" tone="muted">
          Seu link de indicação
        </Text>
        <Card variant="tinted" padding="compact">
          <Text
            ref={linkRef}
            data-testid="referral-link"
            size="sm"
            tone="default"
            className="select-all break-all font-mono"
          >
            {link.url}
          </Text>
        </Card>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button variant="outline" size="md" className="w-full sm:w-auto sm:flex-1" onClick={() => void copyLink()}>
            <Icon as={Copy} size="md" weight="bold" />
            Copiar link
          </Button>
          <ButtonAnchor
            href={whatsAppHref}
            target="_blank"
            rel="noopener noreferrer"
            size="md"
            className="w-full sm:w-auto sm:flex-1"
          >
            <Icon as={WhatsappLogo} size="md" weight="bold" />
            Compartilhar no WhatsApp
          </ButtonAnchor>
        </div>
        <Text role="status" aria-live="polite" size="sm" weight="semibold" tone="default" className="min-h-5">
          {COPY_FEEDBACK[copy]}
        </Text>
      </div>
    </Card>
  )
}
